import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import config from './config';
import { getAircraftModex } from './aircraftModex';

// Theme tokens. Same shape for both themes so getStyles() below stays simple.
const PALETTE = {
    dark: {
        page: '#0b0d10', text: '#f0f3f6', muted: '#9aa6b2',
        headerBorder: '#1f2933',
        thBg: '#1f2933', thText: '#f0f3f6', thBorder: '#2c3641',
        tdBorder: '#1f2933',
        fatalBg: '#161a20', fatalBorder: '#2c3641', codeBg: '#0b0d10',
        // High-contrast zebra: alt rows are noticeably lighter than the
        // page bg so banding is obvious across the room on a TV.
        zebraBase: '#0b0d10',
        zebraAlt: '#2a333d',
        toggleBg: '#1f2933', toggleBorder: '#2c3641', toggleText: '#f0f3f6',
    },
    light: {
        page: '#f4f6f8', text: '#1f2933', muted: '#5a6470',
        headerBorder: '#cdd5dd',
        thBg: '#e9ecef', thText: '#1f2933', thBorder: '#cdd5dd',
        tdBorder: '#dee2e6',
        fatalBg: '#ffffff', fatalBorder: '#cdd5dd', codeBg: '#e9ecef',
        // High-contrast zebra: alt rows are noticeably darker than the
        // page bg so banding is obvious across the room on a TV.
        zebraBase: '#ffffff',
        zebraAlt: '#cdd5de',
        toggleBg: '#ffffff', toggleBorder: '#cdd5dd', toggleText: '#1f2933',
    },
};

// ---------------------------------------------------------------------------
// Aircraft Status TV Dashboard
//
// Public, login-free table dashboard for a TV / compute stick. Replaces the
// legacy /json/dashboard.php?start=0&end=N feed. Auth uses the same
// TVDashboardToken model as /tv-dashboard.
//
//     /aircraft-dashboard?token=<TOKEN>&start=0&end=49
//
//   * token  — required, from Django admin "TV Dashboard Tokens"
//   * start  — index of the first aircraft to display (0-based)
//   * end    — count of aircraft to display (NOT an exclusive index, matching
//              the legacy PHP behavior)
//
// All aircraft for the token's site are sorted by BUNO, then sliced to
// [start, start+end). Row height and font size scale to the viewport so the
// chosen slice always fits on one TV with no scrolling.
// ---------------------------------------------------------------------------

const STATUS_COLORS = {
    UP: '#28a745',
    XC: '#20c997',
    DN: '#dc3545',
    DS: '#dc3545',
    PM: '#ffc107',
    FCF: '#8716bb',
    AWP: '#335d80',
    OTH: '#6c757d',
    DET: '#6c757d',
    AIMD: '#6c757d',
    ACI: '#17a2b8',
    REI: '#6c757d',
    PMC: '#0070c0'
};
const STATUS_TEXT_DARK = new Set(['PM']);
const REFRESH_INTERVAL_MS = 30 * 1000;

const getStyles = (theme = 'dark', fs = 1) => {
    const p = PALETTE[theme] || PALETTE.dark;
    return {
        page: {
            backgroundColor: p.page,
            color: p.text,
            height: '100vh',
            width: '100vw',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'sans-serif',
            padding: '12px 16px',
            boxSizing: 'border-box',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '8px',
            borderBottom: `2px solid ${p.headerBorder}`,
            flex: '0 0 auto',
        },
        title: { fontSize: `${36 * fs}px`, margin: 0, fontWeight: 700 },
        subtitle: { fontSize: `${20 * fs}px`, color: p.muted, marginTop: '2px' },
        headerRight: { display: 'flex', alignItems: 'center', gap: '14px' },
        clock: { fontSize: `${38 * fs}px`, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
        liveBadge: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: `${20 * fs}px`,
            fontWeight: 700,
            padding: '6px 14px',
            borderRadius: '999px',
        },
        liveDot: {
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            animation: 'tvDashPulse 2s infinite',
        },
        themeToggle: {
            cursor: 'pointer',
            fontSize: `${18 * fs}px`,
            fontWeight: 700,
            padding: '6px 14px',
            borderRadius: '999px',
            backgroundColor: p.toggleBg,
            color: p.toggleText,
            border: `1px solid ${p.toggleBorder}`,
            fontFamily: 'inherit',
        },
        tableWrap: {
            flex: '1 1 auto',
            overflow: 'hidden',
            marginTop: '8px',
        },
        table: {
            width: '100%',
            height: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
        },
        th: {
            backgroundColor: p.thBg,
            color: p.thText,
            // Tight vertical padding so the header strip stays compact and
            // hands more vertical space to the data rows.
            padding: '2px 8px',
            textAlign: 'left',
            borderBottom: `2px solid ${p.thBorder}`,
            fontWeight: 700,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            lineHeight: 1.1,
        },
        td: {
            padding: '4px 8px',
            borderBottom: `1px solid ${p.tdBorder}`,
            verticalAlign: 'middle',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
        tdReason: {
            padding: '4px 8px',
            borderBottom: `1px solid ${p.tdBorder}`,
            verticalAlign: 'middle',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            color: p.text,
        },
        // The status column is rendered as a solid-color cell: the entire
        // intersection of row + STATUS column is filled with the status
        // color, no inner pill/badge. Padding is zero so the fill goes
        // edge-to-edge, and the bottom border is removed so adjacent
        // colored cells visually stack as continuous blocks.
        tdStatus: {
            padding: 0,
            verticalAlign: 'middle',
            textAlign: 'center',
            fontWeight: 800,
            letterSpacing: '0.5px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            borderBottom: 'none',
        },
        fatalBox: {
            maxWidth: '720px',
            margin: '15vh auto',
            backgroundColor: p.fatalBg,
            border: `1px solid ${p.fatalBorder}`,
            borderRadius: '12px',
            padding: '32px',
            textAlign: 'center',
        },
        fatalTitle: { fontSize: '28px', fontWeight: 700, marginBottom: '12px' },
        fatalBody: { fontSize: '18px', color: p.muted, lineHeight: 1.6 },
        code: {
            fontFamily: 'monospace',
            backgroundColor: p.codeBg,
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '16px',
        },
        _zebraAlt: p.zebraAlt,
        _zebraBase: p.zebraBase,
        _muted: p.muted,
    };
};


const pulseKeyframes = `
@keyframes tvDashPulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
    100% { opacity: 1; transform: scale(1); }
}`;

if (typeof document !== 'undefined' && !document.getElementById('aircraft-dashboard-keyframes')) {
    const style = document.createElement('style');
    style.id = 'aircraft-dashboard-keyframes';
    style.textContent = pulseKeyframes;
    document.head.appendChild(style);
}

// Compact, uniform date formatter for the EDD/ECD columns.
//
//   - A real date (ISO 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:MM:SS', or anything
//     Date.parse can read) → 'm/d/yy' with no leading zeros.
//       e.g. '2026-05-08' → '5/8/26'
//   - Any non-date text (e.g. 'EDDIW', 'TBD') is returned unchanged.
//   - null / undefined / empty string → ''.
//
// Centralizing this keeps both columns visually uniform regardless of
// whether the field holds a date or a status marker.
const formatShortDate = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value).trim();
    if (!str) return '';

    let date = null;
    const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const y = parseInt(isoMatch[1], 10);
        const m = parseInt(isoMatch[2], 10) - 1;
        const d = parseInt(isoMatch[3], 10);
        const candidate = new Date(y, m, d);
        if (!Number.isNaN(candidate.getTime())) date = candidate;
    } else {
        const candidate = new Date(str);
        if (!Number.isNaN(candidate.getTime())) date = candidate;
    }

    if (!date) return str; // not a date → leave text untouched (e.g. 'EDDIW')

    const m = date.getMonth() + 1;
    const d = date.getDate();
    const yy = String(date.getFullYear() % 100).padStart(2, '0');
    return `${m}/${d}/${yy}`;
};

const daysSinceFly = (dateString) => {
    if (!dateString) return null;
    const dateParts = dateString.split('T')[0].split('-');
    let flyDate;
    if (dateParts.length === 3) {
        flyDate = new Date(
            parseInt(dateParts[0], 10),
            parseInt(dateParts[1], 10) - 1,
            parseInt(dateParts[2], 10),
        );
    } else {
        flyDate = new Date(dateString);
    }
    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const flyMid = new Date(flyDate.getFullYear(), flyDate.getMonth(), flyDate.getDate());
    return Math.floor((todayMid - flyMid) / (1000 * 60 * 60 * 24));
};

// Returns just { backgroundColor, color } for the LFD column. The caller
// spreads these directly onto the <td> so the entire cell is filled with
// the threshold color (matching the STATUS column treatment). Returns
// null when the value is missing or below the 30-day threshold; the
// cell then renders in its normal zebra background.
const daysSinceLfdStyle = (days) => {
    if (days === null || days === undefined) return null;
    if (days >= 90) return { backgroundColor: '#000', color: '#fff' };
    if (days >= 60) return { backgroundColor: '#dc3545', color: '#fff' };
    if (days >= 30) return { backgroundColor: '#ffc107', color: '#000' };
    return null;
};

function getApiBaseURL() {
    const envBase = process.env.REACT_APP_API_BASE_URL || config.API_BASE_URL || '';
    let base = (envBase || '').trim().replace(/\/+$/, '');
    if (!base) return '/api';
    if (!/\/(api)$/.test(base)) base += '/api';
    return base;
}

function AircraftDashboardPage() {
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const token = params.get('token');
    const start = Math.max(0, parseInt(params.get('start') || '0', 10) || 0);
    const endCount = (() => {
        const raw = params.get('end');
        if (raw === null || raw === '') return null;
        const n = parseInt(raw, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
    })();
    // ?fontScale=N multiplies every text size — useful when a particular TV
    // looks slightly too big or small after the auto-fit picks a row height.
    const fontScale = useMemo(() => {
        const raw = params.get('fontScale');
        if (!raw) return 1;
        const n = parseFloat(raw);
        return Number.isFinite(n) && n > 0.25 && n < 4 ? n : 1;
    }, [params]);

    const [data, setData] = useState({ device_name: '', site: null, aircraft: [], generated_at: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [now, setNow] = useState(new Date());
    const [lastUpdated, setLastUpdated] = useState(null);
    const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 1080);
    const failuresRef = useRef(0);

    // We measure the actual height of the table viewport (the flex 1 1 auto
    // container that wraps <table>) rather than guessing from header pixel
    // constants — that way every row fits regardless of header font size,
    // theme padding, or browser zoom.
    const tableWrapRef = useRef(null);
    const theadRef = useRef(null);
    const [tableWrapHeight, setTableWrapHeight] = useState(0);
    const [theadHeight, setTheadHeight] = useState(32);

    // Theme (persisted per-browser under a dashboard-specific key).
    const [theme, setTheme] = useState(() => {
        if (typeof window === 'undefined') return 'dark';
        return window.localStorage.getItem('aircraft-dashboard-theme') || 'dark';
    });
    const toggleTheme = useCallback(() => {
        setTheme((prev) => {
            const next = prev === 'dark' ? 'light' : 'dark';
            try { window.localStorage.setItem('aircraft-dashboard-theme', next); } catch { /* ignore */ }
            return next;
        });
    }, []);
    const styles = useMemo(() => getStyles(theme, fontScale), [theme, fontScale]);

    const fetchData = useCallback(async () => {
        if (!token) return;
        try {
            const response = await axios.get(
                `${getApiBaseURL()}/tv-dashboard/aircraft-board/`,
                { params: { token }, withCredentials: false },
            );
            setData(response.data);
            setLastUpdated(new Date());
            setError(null);
            failuresRef.current = 0;
        } catch (err) {
            failuresRef.current += 1;
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                setError({ kind: 'auth', message: 'Token rejected. Check the URL or generate a new token in Django admin.' });
            } else if (failuresRef.current >= 3) {
                setError({ kind: 'network', message: 'Lost connection to the server. Retrying…' });
            }
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (!token) {
            setError({ kind: 'missing-token', message: 'No token provided.' });
            setLoading(false);
            return undefined;
        }
        fetchData();
        const id = setInterval(fetchData, REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [token, fetchData]);

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const onResize = () => setViewportHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useLayoutEffect(() => {
        const measure = () => {
            if (tableWrapRef.current) setTableWrapHeight(tableWrapRef.current.clientHeight);
            if (theadRef.current) setTheadHeight(theadRef.current.offsetHeight);
        };
        measure();
        const onResize = () => measure();
        window.addEventListener('resize', onResize);
        // Re-measure once after fonts/images settle.
        const id = setTimeout(measure, 200);
        return () => {
            window.removeEventListener('resize', onResize);
            clearTimeout(id);
        };
    }, [data, theme, fontScale, viewportHeight]);

    if (!token) {
        return (
            <div style={styles.page}>
                <div style={styles.fatalBox}>
                    <div style={styles.fatalTitle}>Aircraft Status Dashboard</div>
                    <div style={styles.fatalBody}>
                        Missing token. Open Django admin → <em>TV Dashboard Tokens</em>, create a token,
                        then load this page as <span style={styles.code}>/aircraft-dashboard?token=&lt;TOKEN&gt;&start=0&end=50</span>.
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.fatalBox}>
                    <div style={styles.fatalTitle}>Loading Aircraft Status…</div>
                </div>
            </div>
        );
    }

    if (error && error.kind === 'auth') {
        return (
            <div style={styles.page}>
                <div style={styles.fatalBox}>
                    <div style={styles.fatalTitle}>Token rejected</div>
                    <div style={styles.fatalBody}>{error.message}</div>
                </div>
            </div>
        );
    }

    const fullList = Array.isArray(data.aircraft) ? [...data.aircraft] : [];
    fullList.sort((a, b) => (a.buno || '').localeCompare(b.buno || ''));

    const totalCount = fullList.length;
    const sliceEnd = endCount === null ? totalCount : Math.min(start + endCount, totalCount);
    const displayed = fullList.slice(start, sliceEnd);

    // -- Auto-fit: pick a row height + font size so all rows fit ------------
    // Each <td> has a 1px bottom border that adds to the rendered row height,
    // so subtract `rowCount` from the available pixels to leave room for
    // them. Plus a 2px safety margin so the last row's border isn't clipped.
    const rowCount = Math.max(displayed.length, 1);
    const borderPxPerRow = 1;
    const safetyPx = 2;
    // Fall back to viewport math on first paint, before the layout effect
    // has had a chance to measure the real container.
    const wrapHeight = tableWrapHeight > 0 ? tableWrapHeight : (viewportHeight - 80);
    const available = wrapHeight - theadHeight - (rowCount * borderPxPerRow) - safetyPx;
    const rawRowHeight = available / rowCount;
    // Bigger min row + bigger max row so tiles can grow when there's room.
    const rowHeight = Math.max(18, Math.min(80, rawRowHeight));
    // Bump the font ratio so text fills more of the row, and multiply by
    // the URL fontScale knob.
    const fontSize = Math.max(13, Math.min(38, rowHeight * 0.6)) * fontScale;
    // Headers are intentionally smaller than the body text — they're a
    // visual hint, not the data, so they shouldn't compete with the rows.
    const headerFontSize = Math.max(16, Math.min(18, fontSize * 0.55));
    const statusBadgeFontSize = Math.max(13, Math.min(34, fontSize * 1.0));
    // ---------------------------------------------------------------------

    const liveOk = !error;
    const rangeLabel = totalCount > 0
        ? `${start + 1}-${sliceEnd} of ${totalCount}`
        : '0 of 0';

    return (
        <div style={styles.page}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Aircraft Status</h1>
                    <div style={styles.subtitle}>
                        {data.device_name || 'Aircraft Dashboard'}
                        {data.site ? ` · ${data.site}` : ' · All Sites'}
                        {` · Aircraft ${rangeLabel}`}
                        {lastUpdated && ` · Updated ${lastUpdated.toLocaleTimeString()}`}
                    </div>
                </div>
                <div style={styles.headerRight}>
                    <div
                        style={{
                            ...styles.liveBadge,
                            backgroundColor: liveOk ? 'rgba(40,167,69,0.15)' : 'rgba(220,53,69,0.15)',
                            color: liveOk ? '#28a745' : '#dc3545',
                            border: `1px solid ${liveOk ? '#28a745' : '#dc3545'}`,
                        }}
                    >
                        <span style={{ ...styles.liveDot, backgroundColor: liveOk ? '#28a745' : '#dc3545' }} />
                        {liveOk ? 'LIVE' : 'OFFLINE'}
                    </div>
                    <button
                        type="button"
                        onClick={toggleTheme}
                        style={styles.themeToggle}
                        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                    >
                        {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
                    </button>
                    <div style={styles.clock}>{now.toLocaleTimeString()}</div>
                </div>
            </div>

            <div ref={tableWrapRef} style={styles.tableWrap}>
                <table style={styles.table}>
                    <colgroup>
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '48%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '8%' }} />
                    </colgroup>
                    <thead ref={theadRef}>
                        <tr style={{ height: `${theadHeight}px` }}>
                            <th style={{ ...styles.th, fontSize: `${headerFontSize}px`, textAlign: 'center' }}>MODEX</th>
                            <th style={{ ...styles.th, fontSize: `${headerFontSize}px`, textAlign: 'center' }}>STATUS</th>
                            <th style={{ ...styles.th, fontSize: `${headerFontSize}px` }}>REASON / REMARKS</th>
                            <th style={{ ...styles.th, fontSize: `${headerFontSize}px`, textAlign: 'center' }}>EDD</th>
                            <th style={{ ...styles.th, fontSize: `${headerFontSize}px`, textAlign: 'center' }}>ECD</th>
                            <th style={{ ...styles.th, fontSize: `${headerFontSize}px`, textAlign: 'center' }}>LFD</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayed.length === 0 ? (
                            <tr>
                                <td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: '#9aa6b2', fontStyle: 'italic', padding: '40px' }}>
                                    No aircraft in this range.
                                </td>
                            </tr>
                        ) : (
                            displayed.map((ac, idx) => {
                                const status = ac.status || 'OTH';
                                const statusBg = STATUS_COLORS[status] || '#6c757d';
                                const statusColor = STATUS_TEXT_DARK.has(status) ? '#000' : '#fff';
                                const modex = getAircraftModex(ac);
                                const days = daysSinceFly(ac.last_fly_date);
                                const dayStyle = daysSinceLfdStyle(days);
                                const eddDisplay = ac.edd || (ac.status === 'AWP' ? 'EDDIW' : '');
                                // Zebra-stripe alternating rows for easier scanning on TVs.
                                // Status badge and Days LFD pill stay vivid because they're inline spans.
                                const rowBg = idx % 2 === 0 ? styles._zebraAlt : styles._zebraBase;
                                return (
                                    <tr key={ac.buno} style={{ height: `${rowHeight}px`, backgroundColor: rowBg }}>
                                        <td style={{ ...styles.td, fontSize: `${fontSize}px`, fontWeight: 700, textAlign: 'center' }}>
                                            <span style={{ display: 'inline-block', transform: 'scaleX(1.50)', transformOrigin: 'center' }}>
                                                {modex}
                                            </span>
                                        </td>
                                        <td
                                            style={{
                                                ...styles.tdStatus,
                                                backgroundColor: statusBg,
                                                color: statusColor,
                                                fontSize: `${statusBadgeFontSize}px`,
                                            }}
                                        >
                                            {ac.status || 'N/A'}
                                        </td>
                                        <td style={{ ...styles.tdReason, fontSize: `${fontSize}px` }} title={ac.reason || ''}>
                                            {ac.reason || ''}
                                        </td>
                                        <td style={{ ...styles.td, fontSize: `${fontSize}px`, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                                            {formatShortDate(eddDisplay)}
                                        </td>
                                        <td style={{ ...styles.td, fontSize: `${fontSize}px`, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                                            {formatShortDate(ac.ecd)}
                                        </td>
                                        {dayStyle ? (
                                            <td
                                                style={{
                                                    ...styles.tdStatus,
                                                    ...dayStyle,
                                                    fontSize: `${statusBadgeFontSize}px`,
                                                }}
                                            >
                                                {days}
                                            </td>
                                        ) : (
                                            <td style={{ ...styles.td, fontSize: `${fontSize}px`, textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                                                {days === null ? '' : days}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default AircraftDashboardPage;
